<template>
  <main>
    <vf-header-provider v-if="useCustomHeader" :color-mode="headerStyle" :transparent-mode="overlayHeader" />

    <define-breadcrumbs>
      <vf-breadcrumbs
        v-if="breadcrumbs.length"
        :class="[brandClasses.breadcrumbs, { 'children:!c-white children:hover:!c-white': titleStyle === 'dark' }]"
        :items="breadcrumbs.slice(0, -1)"
        :last-step="showBreadcrumbsLastStep ? breadcrumbs[breadcrumbs.length - 1] : undefined"
        :max-items="breadcrumbsMaxItems"
      />
      <vf-breadcrumbs-skeleton v-else-if="pending" :class="brandClasses.breadcrumbs" />
    </define-breadcrumbs>

    <div :class="brandClasses.container">
      <vf-notification v-if="error" class="<lg:mb-4 lg:mt-4" :dismissible="false" type="error">
        {{ error.message }}
      </vf-notification>
      <breadcrumbs-component v-if="!showBreadcrumbsAfterEspot" />
    </div>

    <div v-if="$feature.enablePLPESpots" class="relative">
      <!-- Only show e-spot on initial load of PLP and not while PLP is being filtered -->
      <div v-if="eSpotsPending" v-style:h="{ sm: '8.5rem', md: '17.25rem', lg: '23.26rem' }" class="skeleton" />
      <cms-section
        v-else-if="eSpots?.['above-grid']"
        :class="brandClasses.eSpotAboveGrid"
        page-context
        :section="eSpots?.['above-grid']"
      />
      <div :class="overlayTitle ? 'w-full bottom-0 absolute' : brandClasses.container">
        <breadcrumbs-component v-if="showBreadcrumbsAfterEspot" />
        <div v-if="!hideTitle" class="mb-3 flex items-end between lg:mb-2">
          <div v-if="pending && !data" class="h-5 w-35 skeleton md:h-8 md:w-45" :class="brandClasses.categoryTitle" />
          <h1
            v-else-if="categoryTitle"
            :class="[brandClasses.categoryTitle, { 'c-white': titleStyle === 'dark' }]"
          >
            {{ categoryTitle }}
          </h1>
          <p aria-live="polite" :class="[brandClasses.productCount, { 'c-white': titleStyle === 'dark' }]">
            {{ data?.total || 0 }} {{ $pluralize($t.products, data?.total || 0) }}
          </p>
        </div>
      </div>
    </div>

    <div :class="brandClasses.container">
      <div v-if="$feature.enablePLPESpots && showAboveGridBelowHeadingEspot">
        <!-- Only show e-spot on initial load of PLP and not while PLP is being filtered -->
        <div v-if="eSpotsPending" v-style:h="{ sm: '8.5rem', md: '17.25rem', lg: '23.26rem' }" class="skeleton" />
        <cms-section
          v-else-if="eSpots?.['above-grid-below-heading']"
          :class="brandClasses.eSpotAboveGridBelowHeading"
          page-context
          :section="eSpots?.['above-grid-below-heading']"
        />
      </div>
      <div v-if="$feature.showFitFinderOnPLP" id="fit-finder-plp" />
    </div>

    <product-list
      v-if="!eSpotsPending"
      :key="productListKey"
      v-slot="{ cols }"
      :current-page="showPaginationLinks ? currentPage : undefined"
      :grid-size-selector="$feature.showGridSizeSelectorOnPLP"
      :header-title="categoryTitle"
      :hide-rating="!$feature.showRatingOnPLP"
      :list="data"
      :next-page="showPaginationLinks ? nextPage : undefined"
      :next-page-loading="pending && infinityLoad"
      :overlay-title="overlayTitle"
      :pickup-filters="$feature.showPickupFiltersOnPLP"
      :previous-page="showPaginationLinks ? previousPage : undefined"
      :products-loading="pending && !infinityLoad"
      :quickshop-mode="$feature.quickshopModeOnPLP"
      :side-filters="$feature.showSideFiltersOnPLP"
      :total-pages="showPaginationLinks ? totalPages : undefined"
      :tracking-category="enCategoryNameWithId"
      @load-more="loadMore"
      @next="loadPage"
      @previous="loadPage"
    >
      <div
        v-for="section in inlineSectionMap(cols)"
        :key="section.name"
        v-style="{
          'grid-col': section.column,
          'grid-row': section.row,
          'display': section.display,
        }"
        :class="brandClasses.eSpotInlineGridWrapper"
      >
        <lazy-cms-section
          :class="getInGridEspotStyle(cols, section.size)"
          full-height
          :section
        />
      </div>
    </product-list>

    <base-lazy-hydrate v-if="$feature.showBloomreachWidget" when="visible">
      <vf-recommendations-widget class="container" type="category" />
    </base-lazy-hydrate>

    <cms-section
      v-if="!eSpotsPending && eSpots?.['lazy-above-footer']"
      :class="brandClasses.eSpotLazyAboveFooter"
      lazy-media
      :page-context="eSpotsWithPageContext?.includes('lazy-above-footer')"
      :section="eSpots?.['lazy-above-footer']"
    />
  </main>
</template>

<script lang="ts" setup>
import type { ContentCategoryEspots, EspotsCategoryData } from '#root/api/clients/content/data-contracts'
import type { Responsive } from '#types/common'
import type { ProductCatalogQuery } from '#types/product'
import { PlpAboveGridBelowHeadingEspotContextKey } from '#content/contexts/plp'

defineOptions({ name: 'ProductCategoryPage' })
definePageMeta({
  validate: async (route) => CATEGORY_URL_REGEX.test(route.path),
  middleware: 'plp-store-filter',
  keepalive: true,
  key: ({ path, query }) => `${path}-${query.page}`
})

const { getUserEventData } = useAnalytics()
const { grid, productsPerPage } = useAppConfig().components.product.list
const {
  brandClasses,
  breadcrumbsMaxItems,
  defaultSort,
  eSpotsWithPageContext,
  hideTitle,
  seoMetaImage,
  showAboveGridBelowHeadingEspot,
  showBreadcrumbsLastStep,
  showBreadcrumbsAfterEspot,
  useCustomHeader
} = useAppConfig().pages.plp
const cart = useCartStore()
const { getSections } = useCms()
const { injectFitFinder } = useFitFinder()
const { $feature, $gtm, $optimizely, $sendExtraMonetateEvents, $seo, $t, $viewport } = useNuxtApp()
const { trackEvent } = useOptimizely()
const route = useRoute()
const slug = useRouteParams('slug')
const filters = useRouteQuery('filters', '')
const page = useRouteQuery('page', '1')
const sort = useRouteQuery('sort', defaultSort)
const { brand } = useRuntimeConfig().public

const storeFilter = $feature.showPickupFiltersOnPLP ? useRouteQuery<string | undefined>('storeFilter') : undefined
const { host } = isServer() ? useRequestHeaders() : window.location
const pageTitle = isServer() ? '' : document.title

const [DefineBreadcrumbs, BreadcrumbsComponent] = createReusableTemplate()

const categoryId = getUrlId(route.path, 'Category')

const eSpots = ref<ContentCategoryEspots['sectionsMap'] | undefined>()
const eSpotsPending = ref(false)
const headerStyle = ref('light')
const infinityLoad = ref(false)
const inlineEspots = ref<any>({})
const isDeactivated = ref(false)
const overlayHeader = ref(false)
const overlayTitle = ref(false)
const productListKey = ref(0)
const titleStyle = ref('light')

const filtersQuery = computed(() => getFiltersQueryString(filters.value))

const query = reactive<ProductCatalogQuery>({
  cgid: categoryId,
  sort: sort.value,
  start: Math.max(+page.value * productsPerPage - productsPerPage, 0),
  count: productsPerPage,
  filters: filtersQuery.value,
  storeFilter: toValue(storeFilter),
  ...(route.query.customTrigger ? { customTrigger: route.query.customTrigger as string } : {})
})

const hasFilters = computed(() => query.filters || query.storeFilter || (query.sort && query.sort !== defaultSort))

const { pending, data, error, refresh } = await useApi().products.catalog(query, {
  lazy: true,
  key: `${categoryId}-${page.value}`,
  transform: (input): typeof input => ({
    ...input,
    filters: getUrlSafeFilters(input.filters),
    products: infinityLoad.value
      ? [
          ...(data.value?.products || []),
          ...input.products
        ]
      : input.products
  }),
})

if (error.value)
  throw createError({ statusCode: error.value.statusCode! >= 500 ? 500 : 404 })

const seoPlaceholderValues = () => ({
  brand: $t.brand,
  brandName: $t.brandName,
  collectionName: data.value?.title || ''
})

const fallbackTitle = computed(() => data.value && replaceAll($seo.plp.title, seoPlaceholderValues()))
const fallbackDescription = computed(() => data.value && replaceAll($seo.plp.description, seoPlaceholderValues()))

const totalPages = computed(() => data.value ? Math.ceil(data.value.total / productsPerPage) : 0)
const currentPage = computed(() => Math.min(+page.value, totalPages.value))
const showPaginationLinks = computed(() => currentPage.value > 1)
const previousPage = computed(() => currentPage.value > 1 ? currentPage.value - 1 : undefined)
const nextPage = computed(() => currentPage.value < totalPages.value ? currentPage.value + 1 : undefined)

watchEffect(() => {
  if (error.value) showError(error.value)
})

useHead(() => ({
  script: [
    {
      innerHTML: [
        data.value?.pageJsonLd,
        data.value?.breadCrumbsJsonLd,
        data.value?.socialMediaJsonLd
      ],
      type: 'application/ld+json',
      tagPosition: 'bodyClose'
    }
  ],
  titleTemplate: data.value?.pageMetaData?.title?.content || fallbackTitle.value || pageTitle,
  link: [
    {
      rel: 'canonical',
      href: `https://${host}${data.value?.url}${+page.value > 1 ? `?page=${page.value}` : ''}`
    },
  ],
  meta: [
    {
      name: 'description',
      content: data.value?.pageMetaData?.description?.content || fallbackDescription.value
    },
    {
      name: 'og:title',
      content: data.value?.pageMetaData?.['og:title']?.content || fallbackTitle.value
    },
    {
      name: 'og:description',
      content: data.value?.pageMetaData?.['og:description']?.content || fallbackDescription.value
    },
    {
      name: 'og:image',
      content: seoMetaImage
    },
    {
      name: 'og:type',
      content: 'website'
    },
    {
      name: 'og:url',
      content: `https://${host}${data.value?.url}`
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image'
    },
    {
      name: 'twitter:creator',
      content: `@${brand}`
    },
    {
      name: 'twitter:site',
      content: `@${brand}`
    },
    ...[
      hasFilters.value || data.value?.pageMetaData?.robots?.content
        ? {
            name: 'robots',
            content: hasFilters.value ? 'noindex' : data.value!.pageMetaData!.robots!.content
          }
        : {}
    ]
  ]
}))

const categoryTitle = computed(() => data.value?.seoTags?.h1 || data.value?.title)
const breadcrumbs = computed(() => data.value?.breadcrumbs || [])
const enCategoryNameWithId = computed(() => formatTrackingCategory(
  data.value?.pageMetaData?.defaultPageName?.content,
  data.value?.url
))

const inlineSectionMap = (cols: Responsive<number>) => {
  if (!$feature.enableInlineGridEspots || !inlineEspots.value?.items) return []

  const items = inlineEspots.value.items
    .flatMap((component) => component.type === 'VfCanvasFilterBy'
      ? filterContentBy(component, route)
      : [component])
    .filter((section) => !(section.hideOnSortOrFilter && hasFilters.value))
    .map((section) => {
      if (section.component) section.component.isFullWidth = true
      return section
    })

  return getResponsivePosition(items, cols, data.value?.products.length || 0)
}

/**
 * @description
 * In grid espot should not be higher than product card height
 * To achieve this we need to add few classes to wrapper
 * The only exclusion is full width espots which could have any height so classes should not be added
 * @param gridCols
 * @param espotSizes
 */
const getInGridEspotStyle = (gridCols: Responsive<number>, espotSizes: Responsive<number>) => {
  // List of classes to avoid Unocss purging
  const styleClasses = {
    sm: gridCols.sm !== espotSizes.sm && '~sm:[&_img]:h-0 ~sm:[&_img]:min-h-full',
    md: gridCols.md !== espotSizes.md && '~md:[&_img]:h-0 ~md:[&_img]:min-h-full',
    lg: gridCols.lg !== espotSizes.lg && '~lg:[&_img]:h-0 ~lg:[&_img]:min-h-full',
  }

  return Object.values(styleClasses).filter(Boolean).join(' ')
}

const loadMore = () => {
  $gtm.push('plpPage.onViewMore')
  query.start += query?.count || 0
  infinityLoad.value = true
}

const loadPage = (page: number) => navigateTo({
  ...route,
  query: {
    ...route.query,
    page
  }
})

const pushImpressions = ({ allProductImpressions = false }: { allProductImpressions?: boolean } = {}) => {
  if (!data.value?.products?.length) return

  const cols = { ...grid.cols, lg: $feature.showSideFiltersOnPLP ? grid.cols.lg - 1 : grid.cols.lg }
  let impressedProducts = data.value.products.map((product, index) => ({ ...product, index })) || []

  if (!allProductImpressions)
    impressedProducts = impressedProducts.slice(-data.value.items)

  const chunkSize = 24
  chunkArray(impressedProducts, chunkSize)
    .forEach((productsChunk, i) => {
      $gtm.push('product.onProductImpression', productsChunk, route.query.filters?.toString() || '', {
        sortOption: (route.query.sort || defaultSort)?.toString() || '',
        layout: cols[$viewport.breakpoint],
        breadcrumbs: breadcrumbs.value,
        offset: query.start + i * chunkSize,
        enCategoryNameWithId: enCategoryNameWithId.value
      })
    })
}

provide(PlpAboveGridBelowHeadingEspotContextKey, { eSpotsPending, eSpot: computed(() => eSpots.value?.['above-grid-below-heading']) })

const pushAllGtmEvents = async ({ allProductImpressions = false }: { allProductImpressions?: boolean } = {}) => {
  // Whether the data is present immediately (SSR) or not (CSR), we check for its value
  await until(() => data.value).toBeTruthy()

  if (data.value?.products?.length) {
    $sendExtraMonetateEvents(data.value, 'monetate:context:ProductThumbnailView')
    trackEvent('category_view', {
      products: data.value?.products,
      categoryName: categoryTitle.value,
      categoryID: categoryId,
      categoryBreadcrumbs: breadcrumbs.value.map(({ label }) => label).join(' | '),
      productsPerPage
    })
  }

  history.replaceState({
    ...(history.state.redirect ? history.state : {}),
    breadcrumbs: serializeAnalyticsBreadcrumbs(breadcrumbs.value),
  }, '')
  setInteractionOrigin()

  $gtm.push('page.onLoadPageData', route, {
    pageTitle: await getPageTitle(),
    pageNameEng: breadcrumbs.value.map(({ defaultName }) => defaultName).join(' — '),
    breadcrumbs: breadcrumbs.value,
    sourceId: data.value?.responseId,
    categoryId
  })
  const userEventData = await getUserEventData()
  $gtm.push('user.onLoadUserData', userEventData)
  $gtm.push('page.onRouterChange', route)
  $gtm.push('cart.onCartLoad', cart)
  $optimizely.dataReady(userEventData)
  trackEvent('page_view', { URL: route.fullPath })
  pushImpressions({ allProductImpressions })
}

const resetCall = async () => {
  query.start = 0
  query.sort = sort.value
  query.filters = filtersQuery.value
  query.storeFilter = toValue(storeFilter)

  await refresh({ dedupe: 'cancel' })
}

if ($feature.enablePLPESpots) {
  eSpotsPending.value = true

  try {
    const content = await getSections.category(categoryId)
    eSpots.value = (content.data.value as EspotsCategoryData)?.content.sectionsMap
    inlineEspots.value = eSpots.value?.['inline-grid'] || {}
    titleStyle.value = content.data.value?.content?.titleStyle || 'light'
    overlayTitle.value = !!content.data.value?.content.overlayTitle
    headerStyle.value = content.data.value?.content?.headerStyle || 'light'
    overlayHeader.value = !!content.data.value?.content.overlayHeader
  }
  catch (e) {
    log.error(e)
  }

  eSpotsPending.value = false
}

watch(pending, () => {
  if (isServer() || pending.value || !data.value?.products?.length || data.value?.products?.length <= productsPerPage)
    return

  $sendExtraMonetateEvents(data.value?.products, 'monetate:context:ProductThumbnailView')
  trackEvent('category_view', {
    products: data.value?.products,
    categoryName: categoryTitle.value,
    categoryID: categoryId,
    categoryBreadcrumbs: breadcrumbs.value.map(({ label }) => label).join(' | '),
  })
})

watch([filters, sort, storeFilter, slug], (
  [newFilters, newSort, newStoreFilter, newSlug],
  [oldFilters, oldSort, oldStoreFilter, oldSlug]
) => {
  if (newSlug?.[0] !== oldSlug?.[0]) return
  if (newSort === oldSort && newStoreFilter === oldStoreFilter && newFilters === oldFilters) return
  if (isDeactivated.value) return

  // cover scenarios when you navigate in sameCategory and filters section is not sync
  if (Object.keys(route.query).length === 0) productListKey.value++

  query.start = 0
  query.sort = sort.value
  query.filters = filtersQuery.value
  query.storeFilter = toValue(storeFilter)
  infinityLoad.value = false
})

onMounted(() => {
  // Push impressions after the component is mounted without a cache
  pushAllGtmEvents()
  injectFitFinder()
})

onActivated(async () => {
  isDeactivated.value = false
  if (
    query.sort !== sort.value
    || query.filters !== filtersQuery.value
    || query.storeFilter !== toValue(storeFilter)
  ) {
    productListKey.value++
    await resetCall()
  }
  // Push impressions after restoring from keep-alive cache
  await pushAllGtmEvents({ allProductImpressions: true })
  injectFitFinder()
})

onBeforeUnmount (() => data.value = null)

// Sets the flag to prevent pushing impressions on reactivation
onDeactivated(() => isDeactivated.value = true)
</script>
